import React, { useMemo, useState } from 'react';
import CruxInsight from '../components/CruxInsight';
import Card from '../design-system/Card';
import MetricCard from '../components/MetricCard';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import useGrowthData from '../hooks/useGrowthData';
import { generateGrowthInsight } from '../utils/insightGen';
import { colors, spacing, fontStack } from '../design-system/tokens';

import Interview from './tabs/Interview';
import Garden from './tabs/Garden';
import ManageSkillsModal from './ManageSkillsModal';
import CVUploadModal from './CVUploadModal';

/**
 * Growth module router — Phase 3.
 *   /growth              → GrowthHome (new light UI)
 *   /growth/interview    → existing Interview tab (kept functional)
 *   /growth/garden       → existing Garden tab (kept, de-emphasized)
 *   /growth/copilot      → redirected to /copilot (handled in App.js redirects)
 *   /growth/mirror       → not surfaced (Mirror was already disabled)
 *
 * The old "SKILL PULSE" dark wrapper, including the radar + trajectory
 * visuals, is archived at src/Growth/_archive/SkillPulseWrapper.old.js
 * for future revival in light theme.
 */
export default function Growth() {
  const path = (typeof window !== 'undefined' && window.location.pathname) || '/growth';

  if (path === '/growth/interview') return <Interview />;
  if (path === '/growth/garden')    return <Garden />;
  // Anything else under /growth falls through to the new home.
  return <GrowthHome />;
}

/* ================================================================
   GrowthHome
   ================================================================ */

function GrowthHome() {
  const { userId, currentSkills, allRows, lastInterviewAt, loading, error, refresh } = useGrowthData();
  const [manageOpen, setManageOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toast, setToast] = useState(null); // { message }

  const insight = useMemo(
    () => generateGrowthInsight({ currentSkills, allRows, lastInterviewAt }),
    [currentSkills, allRows, lastInterviewAt]
  );

  const overview = useMemo(() => buildOverview(currentSkills), [currentSkills]);
  const roadmap = useMemo(
    () => buildRoadmap(currentSkills, lastInterviewAt),
    [currentSkills, lastInterviewAt]
  );
  const todaysFocus = useMemo(
    () => buildTodaysFocus(currentSkills, lastInterviewAt),
    [currentSkills, lastInterviewAt]
  );

  const isInitialLoading = loading && currentSkills.length === 0 && !error;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      {/* Page header — small "Manage skills" CTA top-right, above the Insight. */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          fontFamily: fontStack,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 600,
              color: colors.textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            Growth
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: colors.textSecondary }}>
            {currentSkills.length > 0
              ? `${currentSkills.length} skill${currentSkills.length === 1 ? '' : 's'} tracked.`
              : 'Add a few skills to ground your insight.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            style={manageBtnStyle}
          >
            Upload CV
          </button>
          <button
            type="button"
            onClick={() => setManageOpen(true)}
            style={manageBtnStyle}
          >
            Manage skills
          </button>
        </div>
      </header>

      {isInitialLoading ? (
        <LoadingState rows={3} label="Loading growth insight" />
      ) : (
        <CruxInsight
          state={insight.state}
          actions={insight.actions}
          reasons={insight.reasons}
          confidence={insight.confidence}
          accent={colors.accentGrowth}
        />
      )}

      <SkillOverview overview={overview} hasData={currentSkills.length > 0} />
      <Roadmap roadmap={roadmap} hasData={currentSkills.length > 0} />
      <TodaysFocus focus={todaysFocus} />

      {error && (
        <ErrorState
          title="Some data couldn't load"
          message={error}
          onRetry={refresh}
          inline
        />
      )}

      <ManageSkillsModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        userId={userId}
        currentSkills={currentSkills}
        onChanged={refresh}
      />

      <CVUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        userId={userId}
        onSaved={async ({ count }) => {
          await refresh();
          setToast({
            message: `Added ${count} skill${count === 1 ? '' : 's'} from your CV.`,
          });
          setTimeout(() => setToast(null), 2400);
        }}
      />

      {toast && <Toast message={toast.message} />}
    </div>
  );
}

function Toast({ message }) {
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 18px',
        borderRadius: 999,
        backgroundColor: colors.textPrimary,
        color: '#fff',
        fontSize: 13,
        fontWeight: 500,
        fontFamily: fontStack,
        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
        zIndex: 220,
      }}
    >
      {message}
    </div>
  );
}

const manageBtnStyle = {
  padding: '10px 18px',
  minHeight: 40,
  borderRadius: 999,
  border: `1px solid ${colors.primary}`,
  background: 'transparent',
  color: colors.primary,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

/* ================================================================
   B. Skill Overview
   ================================================================ */

function buildOverview(currentSkills) {
  if (!currentSkills.length) {
    return { overall: null, top: null, focus: null };
  }
  const avg = Math.round(
    currentSkills.reduce((a, b) => a + (Number(b.score) || 0), 0) / currentSkills.length
  );
  const top = currentSkills.reduce((a, b) =>
    (Number(b.score) || 0) > (Number(a.score) || 0) ? b : a
  );
  const focus = currentSkills.reduce((a, b) =>
    (Number(b.score) || 0) < (Number(a.score) || 0) ? b : a
  );
  return { overall: avg, top, focus };
}

function SkillOverview({ overview, hasData }) {
  if (!hasData) {
    return (
      <Card padding={spacing.lg}>
        <SectionTitle>Skill Overview</SectionTitle>
        <EmptyState message="Add a few skills with self-rated scores to populate the overview." />
      </Card>
    );
  }
  return (
    <div className="growth-overview-grid">
      <MetricCard
        label="Overall"
        value={`${overview.overall}/100`}
        subtitle={overallSubtitle(overview.overall)}
        accent={colors.accentGrowth}
      />
      <MetricCard
        label="Top skill"
        value={overview.top.skill_name}
        subtitle={`${overview.top.score}/100 · ${categoryLabel(overview.top.category)}`}
        accent={colors.accentGrowth}
      />
      <MetricCard
        label="Focus area"
        value={overview.focus.skill_name}
        subtitle={`${overview.focus.score}/100 · ${categoryLabel(overview.focus.category)}`}
        accent="#E0B43A"
      />
      <style>{`
        .growth-overview-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        @media (max-width: 1024px) {
          .growth-overview-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 768px) {
          .growth-overview-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

function overallSubtitle(avg) {
  if (avg >= 80) return 'Expert band overall';
  if (avg >= 60) return 'Advanced band overall';
  if (avg >= 40) return 'Intermediate band overall';
  return 'Beginner band overall';
}

function categoryLabel(cat) {
  const map = {
    technical: 'Technical',
    leadership: 'Leadership',
    communication: 'Communication',
    domain: 'Domain',
    other: 'Other',
  };
  return map[String(cat || '').toLowerCase()] || 'Other';
}

/* ================================================================
   C. Roadmap (NOW / NEXT / LATER)
   ================================================================ */

function buildRoadmap(currentSkills, lastInterviewAt) {
  if (!currentSkills.length) {
    return { now: [], next: [], later: [] };
  }
  const sorted = [...currentSkills].sort(
    (a, b) => (Number(a.score) || 0) - (Number(b.score) || 0)
  );
  const weakest = sorted[0];
  const secondWeakest = sorted[1];
  const top = sorted[sorted.length - 1];

  const daysSinceInterview = lastInterviewAt
    ? Math.floor((Date.now() - new Date(lastInterviewAt).getTime()) / (24 * 60 * 60 * 1000))
    : null;

  const now = [];
  if (Number(weakest.score) < 50)
    now.push(`Run a focused 30-minute block on ${weakest.skill_name}`);
  if (daysSinceInterview === null || daysSinceInterview >= 7)
    now.push('Do one 25-minute interview rep');
  if (now.length === 0) now.push(`Stretch one rep beyond comfort on ${top.skill_name}`);

  const next = [];
  if (secondWeakest && Number(secondWeakest.score) < 60)
    next.push(`Schedule two practice sessions for ${secondWeakest.skill_name} this week`);
  next.push('Re-rate the skill you practiced after each session');
  next.push('Add one new skill you want to grow but haven’t logged yet');

  const later = [];
  later.push(`Reach Advanced (60+) on ${weakest.skill_name}`);
  if (Number(top.score) < 80)
    later.push(`Push ${top.skill_name} into Expert band (80+)`);
  later.push('Build a portfolio artefact in your strongest category');

  return { now: cap(now, 2), next: cap(next, 3), later: cap(later, 3) };
}

function cap(arr, n) {
  return arr.slice(0, n);
}

function Roadmap({ roadmap, hasData }) {
  if (!hasData) return null;
  return (
    <Card padding={spacing.lg}>
      <SectionTitle>Roadmap</SectionTitle>
      <div className="growth-roadmap-grid">
        <RoadmapColumn label="Now"   items={roadmap.now}   accent={colors.accentGrowth} />
        <RoadmapColumn label="Next"  items={roadmap.next}  accent={colors.textSecondary} />
        <RoadmapColumn label="Later" items={roadmap.later} accent={colors.textSecondary} />
      </div>
      <style>{`
        .growth-roadmap-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 24px;
          margin-top: 12px;
        }
        @media (max-width: 768px) {
          .growth-roadmap-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>
    </Card>
  );
}

function RoadmapColumn({ label, items, accent }) {
  return (
    <div style={{ fontFamily: fontStack }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: accent,
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {items.map((item, i) => (
          <li
            key={i}
            style={{
              fontSize: 14,
              color: colors.textPrimary,
              lineHeight: 1.5,
              paddingLeft: 12,
              borderLeft: `2px solid ${colors.border}`,
            }}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ================================================================
   D. Today's Focus
   ================================================================ */

function buildTodaysFocus(currentSkills, lastInterviewAt) {
  const daysSinceInterview = lastInterviewAt
    ? Math.floor((Date.now() - new Date(lastInterviewAt).getTime()) / (24 * 60 * 60 * 1000))
    : null;

  if (daysSinceInterview === null) {
    return {
      headline: 'Try a 25-minute interview rep',
      detail: 'Your first rep calibrates everything. Pick a skill, set a timer, talk through a real problem.',
      cta: { label: 'Start Interview', href: '/growth/interview' },
    };
  }
  if (daysSinceInterview >= 7) {
    return {
      headline: `${daysSinceInterview} days since your last interview rep`,
      detail: 'A short rep today resets the rhythm and surfaces fresh feedback.',
      cta: { label: 'Start Interview', href: '/growth/interview' },
    };
  }
  if (currentSkills.length > 0) {
    const sorted = [...currentSkills].sort(
      (a, b) => (Number(a.score) || 0) - (Number(b.score) || 0)
    );
    const focus = sorted[0];
    return {
      headline: `Spend 25 minutes on ${focus.skill_name}`,
      detail: `Currently at ${focus.score}/100 — one focused block moves the needle more than three distracted ones.`,
      cta: { label: 'Start Interview', href: '/growth/interview' },
    };
  }
  return {
    headline: 'Try a 25-minute interview rep',
    detail: 'Pick the skill you most want to sharpen. Set a timer. Begin.',
    cta: { label: 'Start Interview', href: '/growth/interview' },
  };
}

function TodaysFocus({ focus }) {
  return (
    <Card padding={spacing.lg} accentColor={colors.accentGrowth}>
      <SectionTitle>Today’s Focus</SectionTitle>
      <h3
        style={{
          margin: 0,
          marginTop: 6,
          fontSize: 20,
          fontWeight: 600,
          color: colors.textPrimary,
          fontFamily: fontStack,
          lineHeight: 1.35,
        }}
      >
        {focus.headline}
      </h3>
      <p
        style={{
          margin: '8px 0 16px',
          fontSize: 14,
          color: colors.textSecondary,
          fontFamily: fontStack,
          lineHeight: 1.5,
        }}
      >
        {focus.detail}
      </p>
      <a
        href={focus.cta.href}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 20px',
          borderRadius: 999,
          backgroundColor: colors.primary,
          color: '#fff',
          fontFamily: fontStack,
          fontSize: 14,
          fontWeight: 600,
          textDecoration: 'none',
          boxShadow: '0 4px 12px rgba(47, 91, 255, 0.18)',
        }}
      >
        {focus.cta.label} <span aria-hidden="true">→</span>
      </a>
    </Card>
  );
}

/* ================================================================
   shared
   ================================================================ */

function SectionTitle({ children }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: 14,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: colors.textSecondary,
        fontWeight: 600,
        fontFamily: fontStack,
      }}
    >
      {children}
    </h3>
  );
}
